import { IObservable, ISubscription, Subject } from '~/rage/utils';
import {
	IKeyConfig,
	InputAction,
	InputComboAction,
	InputKey,
	getInputActionConfig,
	getInputComboActionConfig,
	getInputKeyCombinationConfig,
	getInputKeyConfig,
} from '~/shared';
import { IInputService } from './inputServiceTypes';

export abstract class AbstractInputService implements IInputService {
	private keyStates = new Map<number, Subject<boolean>>();
	private keyCombinations = new Map<InputComboAction, Subject<boolean>>();
	private proxiedKeySubjects = new Set<number>();

	private clipboard = new Subject<string | null>(null);

	private isAnyInputFocused = new Subject<boolean>(false);

	constructor() {
		window.addEventListener('keydown', (e) => {
			// Для модификаторов (Shift, Ctrl, Alt) используем location или code для различения левого/правого
			let keyCode = e.keyCode;
			if (e.keyCode === 16) {
				if (e.location === 1 || e.code === 'ShiftLeft') {
					keyCode = 160;
				} else if (e.location === 2 || e.code === 'ShiftRight') {
					keyCode = 161;
				} else {
					keyCode = 160;
				}
			} else if (e.keyCode === 17) {
				if (e.location === 1 || e.code === 'ControlLeft') {
					keyCode = 162;
				} else if (e.location === 2 || e.code === 'ControlRight') {
					keyCode = 163;
				} else {
					keyCode = 162;
				}
			} else if (e.keyCode === 18) {
				if (e.location === 1 || e.code === 'AltLeft') {
					keyCode = 164;
				} else if (e.location === 2 || e.code === 'AltRight') {
					keyCode = 165;
				} else {
					keyCode = 164;
				}
			}

			const keySubject = this.keyStates.get(keyCode);
			if (keySubject && !keySubject.getValue()) {
				keySubject.next(true);
			}
		});

		window.addEventListener('keyup', (e) => {
			// Для модификаторов (Shift, Ctrl, Alt) используем location или code для различения левого/правого
			let keyCode = e.keyCode;
			if (e.keyCode === 16) {
				if (e.location === 1 || e.code === 'ShiftLeft') {
					keyCode = 160;
				} else if (e.location === 2 || e.code === 'ShiftRight') {
					keyCode = 161;
				} else {
					keyCode = 160;
				}
			} else if (e.keyCode === 17) {
				if (e.location === 1 || e.code === 'ControlLeft') {
					keyCode = 162;
				} else if (e.location === 2 || e.code === 'ControlRight') {
					keyCode = 163;
				} else {
					keyCode = 162;
				}
			} else if (e.keyCode === 18) {
				if (e.location === 1 || e.code === 'AltLeft') {
					keyCode = 164;
				} else if (e.location === 2 || e.code === 'AltRight') {
					keyCode = 165;
				} else {
					keyCode = 164;
				}
			}

			const keySubject = this.keyStates.get(keyCode);
			if (keySubject && keySubject.getValue()) {
				keySubject.next(false);
			}
		});
	}

	private getKeyState(keyConfig: IKeyConfig): Subject<boolean> {
		let keySubject = this.keyStates.get(keyConfig.code);
		if (!keySubject) {
			keySubject = new Subject<boolean>(false);
			this.keyStates.set(keyConfig.code, keySubject);
		}

		if (
			keyConfig.keyName === InputKey.Escape &&
			!this.proxiedKeySubjects.has(keyConfig.code)
		) {
			this.proxiedKeySubjects.add(keyConfig.code);

			keySubject.subscribe = new Proxy(keySubject.subscribe, {
				apply: (target, thisArg, args: any) => {
					if (keySubject?.getCount() === 0) {
						this.setEscButtonEnabled(false);
					}

					return target.apply(thisArg, args);
				},
			});

			keySubject.unsubscribe = new Proxy(keySubject.unsubscribe, {
				apply: (target, thisArg, args: any) => {
					target.apply(thisArg, args);

					if (keySubject?.getCount() === 0) {
						this.setEscButtonEnabled(true);
					}
				},
			});
		}

		return keySubject;
	}

	public getActionState(action: InputAction): IObservable<boolean> {
		const actionConfig = getInputActionConfig(action);
		const keyConfig = getInputKeyConfig(actionConfig.defaultKey);
		const keyState = this.getKeyState(keyConfig);
		return keyState;
	}

	public getComboActionState(action: InputComboAction): IObservable<boolean> {
		const actionConfig = getInputComboActionConfig(action);
		const keyConfigs = getInputKeyCombinationConfig(actionConfig.defaultKeys);
		const keyStates = keyConfigs.map((keyConfig) =>
			this.getKeyState(keyConfig),
		);
		if (keyStates.length < 2) {
			throw new Error(`action ${action} does not contain multiple keys`);
		}

		let keyCombinationState = this.keyCombinations.get(action);
		if (!keyCombinationState) {
			keyCombinationState = new Subject<boolean>(false);
			const keyPressStates: boolean[] = Array(keyStates.length).fill(false);
			const keyTimestamps: number[] = Array(keyStates.length).fill(0);
			const keySubs: ISubscription[] = [];

			keyStates.forEach((keyState, index) => {
				const keySub = keyState.subscribe((state) => {
					keyPressStates[index] = state;
					keyTimestamps[index] = Date.now();

					if (index === keyStates.length - 1) {
						const keysPressed = keyStates
							.map((sub) => sub.getValue())
							.reduce((prev, curr) => prev && curr);
						const keysInSequence = keyTimestamps.every(
							(value, index, array) => index === 0 || value >= array[index - 1],
						);
						keyCombinationState!.next(keysPressed && keysInSequence);
					}
				});
				keySubs.push(keySub);
			});

			keyCombinationState.unsubscribe = new Proxy(
				keyCombinationState.unsubscribe,
				{
					apply: (target, thisArg, args: any) => {
						target.apply(thisArg, args);

						if (keyCombinationState?.getCount() === 0) {
							keySubs.forEach((sub) => sub.unsubscribe());
							this.keyCombinations.delete(action);
						}
					},
				},
			);

			this.keyCombinations.set(action, keyCombinationState);
		}

		return keyCombinationState;
	}

	public getKey(action: InputAction): InputKey {
		const config = getInputActionConfig(action);
		return config.defaultKey;
	}

	public isDown(action: InputAction): boolean {
		return this.getActionState(action).getValue();
	}

	public getIsAnyInputFocused(): IObservable<boolean> {
		return this.isAnyInputFocused;
	}

	protected setIsAnyInputFocusedLocal(isFocused: boolean): void {
		this.isAnyInputFocused.next(isFocused);
	}

	public abstract setEscButtonEnabled(isEnabled: boolean): Promise<void>;

	public getClipboardText(): IObservable<string | null> {
		return this.clipboard;
	}

	public copyToClipboard(text: string | null): void {
		this.clipboard.next(text);
	}

	public abstract setAnyInputFocused(isFocused: boolean): Promise<void>;
}
